import { db } from "../../firebase";
import {
  collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, serverTimestamp
} from "firebase/firestore";
import {
  SUBJECTS_FETCH_REQUEST, SUBJECTS_FETCH_SUCCESS, SUBJECTS_FETCH_FAILURE,
  SUBJECT_CREATE_REQUEST, SUBJECT_CREATE_SUCCESS, SUBJECT_CREATE_FAILURE,
  SUBJECT_UPDATE_REQUEST, SUBJECT_UPDATE_SUCCESS, SUBJECT_UPDATE_FAILURE,
  SUBJECT_DELETE_REQUEST, SUBJECT_DELETE_SUCCESS, SUBJECT_DELETE_FAILURE,
  UNITS_FETCH_REQUEST, UNITS_FETCH_SUCCESS, UNITS_FETCH_FAILURE,
  UNIT_CREATE_REQUEST, UNIT_CREATE_SUCCESS, UNIT_CREATE_FAILURE,
  UNIT_UPDATE_REQUEST, UNIT_UPDATE_SUCCESS, UNIT_UPDATE_FAILURE,
  UNIT_DELETE_REQUEST, UNIT_DELETE_SUCCESS, UNIT_DELETE_FAILURE,
  TOPICS_FETCH_REQUEST, TOPICS_FETCH_SUCCESS, TOPICS_FETCH_FAILURE,
  TOPIC_CREATE_REQUEST, TOPIC_CREATE_SUCCESS, TOPIC_CREATE_FAILURE,
  TOPIC_UPDATE_REQUEST, TOPIC_UPDATE_SUCCESS, TOPIC_UPDATE_FAILURE,
  TOPIC_DELETE_REQUEST, TOPIC_DELETE_SUCCESS, TOPIC_DELETE_FAILURE,
  SUBJECTS_UNITS_RESET
} from "./SubjectsUnitsTypes";

// Reset
export const resetSubjectsUnits = () => ({ type: SUBJECTS_UNITS_RESET });

const SUBJECTS_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

// ========== SUBJECTS ACTIONS ==========

// Fetch all subjects
export const fetchSubjects = () => {
  return async (dispatch, getState) => {
    try {
      // TTL staleness check
      const state = getState().questionBankSubjects;
      if (
        state.subjects.length > 0 &&
        state.lastFetchedAt &&
        Date.now() - state.lastFetchedAt < SUBJECTS_CACHE_TTL
      ) {
        return;
      }

      dispatch({ type: SUBJECTS_FETCH_REQUEST });

      const subjectsRef = collection(db, "questionBankSubjects");
      const snapshot = await getDocs(subjectsRef);

      const subjects = snapshot.docs.map(doc => ({
        subjectId: doc.id,
        ...doc.data()
      }));

      // Sort client-side by name
      subjects.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      dispatch({ type: SUBJECTS_FETCH_SUCCESS, payload: subjects });
    } catch (error) {
      dispatch({ type: SUBJECTS_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Create subject
export const createSubject = (subjectData, userEmail) => {
  return async (dispatch) => {
    try {
      dispatch({ type: SUBJECT_CREATE_REQUEST });

      const subjectsRef = collection(db, "questionBankSubjects");
      const newSubject = {
        name: subjectData.name,
        description: subjectData.description || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userEmail,
        isActive: true
      };

      const docRef = await addDoc(subjectsRef, newSubject);

      const createdSubject = {
        subjectId: docRef.id,
        ...newSubject,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: SUBJECT_CREATE_SUCCESS, payload: createdSubject });
      return { success: true, subjectId: docRef.id };
    } catch (error) {
      dispatch({ type: SUBJECT_CREATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Update subject
export const updateSubject = (subjectId, subjectData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: SUBJECT_UPDATE_REQUEST });

      const subjectRef = doc(db, "questionBankSubjects", subjectId);
      const updateData = {
        ...subjectData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(subjectRef, updateData);

      const updatedSubject = {
        subjectId,
        ...subjectData,
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: SUBJECT_UPDATE_SUCCESS, payload: updatedSubject });
      return { success: true };
    } catch (error) {
      dispatch({ type: SUBJECT_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Delete subject (check if it has units or questions first)
export const deleteSubject = (subjectId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: SUBJECT_DELETE_REQUEST });

      // Check if subject has units
      const unitsRef = collection(db, "questionBankUnits");
      const unitsQuery = query(unitsRef, where("subjectId", "==", subjectId));
      const unitsSnapshot = await getDocs(unitsQuery);

      if (!unitsSnapshot.empty) {
        throw new Error("Cannot delete subject with existing units. Please delete all units first.");
      }

      // Check if subject has questions
      const questionsRef = collection(db, "questionBankQuestions");
      const questionsQuery = query(questionsRef, where("subjectId", "==", subjectId));
      const questionsSnapshot = await getDocs(questionsQuery);

      if (!questionsSnapshot.empty) {
        throw new Error("Cannot delete subject with existing questions. Please delete all questions first.");
      }

      // Check if subject has passages
      const passagesRef = collection(db, "questionBankPassages");
      const passagesQuery = query(passagesRef, where("subjectId", "==", subjectId));
      const passagesSnapshot = await getDocs(passagesQuery);

      if (!passagesSnapshot.empty) {
        throw new Error("Cannot delete subject with existing passages. Please delete all passages first.");
      }

      // Delete subject
      const subjectRef = doc(db, "questionBankSubjects", subjectId);
      await deleteDoc(subjectRef);

      dispatch({ type: SUBJECT_DELETE_SUCCESS, payload: subjectId });
      return { success: true };
    } catch (error) {
      dispatch({ type: SUBJECT_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// ========== UNITS ACTIONS ==========

// Fetch all units (optionally filter by subjectId)
export const fetchUnits = (subjectId = null) => {
  return async (dispatch, getState) => {
    try {
      // TTL staleness check (only when fetching ALL)
      if (!subjectId) {
        const state = getState().questionBankUnits;
        if (
          state.units.length > 0 &&
          state.lastFetchedAt &&
          Date.now() - state.lastFetchedAt < SUBJECTS_CACHE_TTL
        ) {
          return;
        }
      }

      dispatch({ type: UNITS_FETCH_REQUEST });

      const unitsRef = collection(db, "questionBankUnits");
      let q;

      if (subjectId) {
        q = query(unitsRef, where("subjectId", "==", subjectId));
      } else {
        q = query(unitsRef);
      }

      const snapshot = await getDocs(q);

      const units = snapshot.docs.map(doc => ({
        unitId: doc.id,
        ...doc.data()
      }));

      // Sort client-side by name
      units.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      dispatch({ type: UNITS_FETCH_SUCCESS, payload: units });
    } catch (error) {
      dispatch({ type: UNITS_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Create unit
export const createUnit = (unitData, userEmail) => {
  return async (dispatch) => {
    try {
      dispatch({ type: UNIT_CREATE_REQUEST });

      const unitsRef = collection(db, "questionBankUnits");
      const newUnit = {
        subjectId: unitData.subjectId,
        name: unitData.name,
        description: unitData.description || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userEmail,
        isActive: true
      };

      const docRef = await addDoc(unitsRef, newUnit);

      const createdUnit = {
        unitId: docRef.id,
        ...newUnit,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: UNIT_CREATE_SUCCESS, payload: createdUnit });
      return { success: true, unitId: docRef.id };
    } catch (error) {
      dispatch({ type: UNIT_CREATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Update unit
export const updateUnit = (unitId, unitData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: UNIT_UPDATE_REQUEST });

      const unitRef = doc(db, "questionBankUnits", unitId);
      const updateData = {
        ...unitData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(unitRef, updateData);

      const updatedUnit = {
        unitId,
        ...unitData,
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: UNIT_UPDATE_SUCCESS, payload: updatedUnit });
      return { success: true };
    } catch (error) {
      dispatch({ type: UNIT_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Delete unit (check if it has questions first)
export const deleteUnit = (unitId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: UNIT_DELETE_REQUEST });

      // Check if unit has questions
      const questionsRef = collection(db, "questionBankQuestions");
      const questionsQuery = query(questionsRef, where("unitId", "==", unitId));
      const questionsSnapshot = await getDocs(questionsQuery);

      if (!questionsSnapshot.empty) {
        throw new Error("Cannot delete unit with existing questions. Please delete all questions first.");
      }

      // Check if unit has passages
      const passagesRef = collection(db, "questionBankPassages");
      const passagesQuery = query(passagesRef, where("unitId", "==", unitId));
      const passagesSnapshot = await getDocs(passagesQuery);

      if (!passagesSnapshot.empty) {
        throw new Error("Cannot delete unit with existing passages. Please delete all passages first.");
      }

      // Delete unit
      const unitRef = doc(db, "questionBankUnits", unitId);
      await deleteDoc(unitRef);

      dispatch({ type: UNIT_DELETE_SUCCESS, payload: unitId });
      return { success: true };
    } catch (error) {
      dispatch({ type: UNIT_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// ========== TOPICS ACTIONS ==========

// Fetch all topics (optionally filter by unitId)
export const fetchTopics = (unitId = null) => {
  return async (dispatch, getState) => {
    try {
      // TTL staleness check (only when fetching ALL)
      if (!unitId) {
        const state = getState().questionBankTopics;
        if (
          state.topics.length > 0 &&
          state.lastFetchedAt &&
          Date.now() - state.lastFetchedAt < SUBJECTS_CACHE_TTL
        ) {
          return;
        }
      }

      dispatch({ type: TOPICS_FETCH_REQUEST });

      const topicsRef = collection(db, "questionBankTopics");
      let q;

      if (unitId) {
        q = query(topicsRef, where("unitId", "==", unitId));
      } else {
        q = query(topicsRef);
      }

      const snapshot = await getDocs(q);

      const topics = snapshot.docs.map(doc => ({
        topicId: doc.id,
        ...doc.data()
      }));

      // Sort client-side by name
      topics.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      dispatch({ type: TOPICS_FETCH_SUCCESS, payload: topics });
    } catch (error) {
      dispatch({ type: TOPICS_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Create topic
export const createTopic = (topicData, userEmail) => {
  return async (dispatch) => {
    try {
      dispatch({ type: TOPIC_CREATE_REQUEST });

      const topicsRef = collection(db, "questionBankTopics");
      const newTopic = {
        unitId: topicData.unitId,
        subjectId: topicData.subjectId,
        name: topicData.name,
        description: topicData.description || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userEmail,
        isActive: true
      };

      const docRef = await addDoc(topicsRef, newTopic);

      const createdTopic = {
        topicId: docRef.id,
        ...newTopic,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: TOPIC_CREATE_SUCCESS, payload: createdTopic });
      return { success: true, topicId: docRef.id };
    } catch (error) {
      dispatch({ type: TOPIC_CREATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Update topic
export const updateTopic = (topicId, topicData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: TOPIC_UPDATE_REQUEST });

      const topicRef = doc(db, "questionBankTopics", topicId);
      const updateData = {
        ...topicData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(topicRef, updateData);

      const updatedTopic = {
        topicId,
        ...topicData,
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: TOPIC_UPDATE_SUCCESS, payload: updatedTopic });
      return { success: true };
    } catch (error) {
      dispatch({ type: TOPIC_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Delete topic (check if it has questions first)
export const deleteTopic = (topicId) => {
  return async (dispatch) => {
    try {
      dispatch({ type: TOPIC_DELETE_REQUEST });

      // Check if topic has questions
      const questionsRef = collection(db, "questionBankQuestions");
      const questionsQuery = query(questionsRef, where("topicId", "==", topicId));
      const questionsSnapshot = await getDocs(questionsQuery);

      if (!questionsSnapshot.empty) {
        throw new Error("Cannot delete topic with existing questions. Please delete all questions first.");
      }

      // Delete topic
      const topicRef = doc(db, "questionBankTopics", topicId);
      await deleteDoc(topicRef);

      dispatch({ type: TOPIC_DELETE_SUCCESS, payload: topicId });
      return { success: true };
    } catch (error) {
      dispatch({ type: TOPIC_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};
